import React, { useEffect, useState } from "react";
import { Button, Chip, Flex, Heading, Modal, Notification, Spinner, Text } from "@telia/teddy";
import { useSelector } from "react-redux";
import { API_URL_GET_SALES_AGENTS } from "../../api";
import { retailerFetch } from "../../retailerFetch";
import { RootState } from "../../../../../models";
import { getSalesAgent } from "../../state/selectors/selectors";
import CreateSalesAgentModal from "./CreateSalesAgentModal";
import SetPasswordModal from "./SetPasswordModal";
import { SalesAgent } from "../../types/models";
import { useSalesAgentActions } from "../../hooks/useSalesAgentActions";
import "./Selgere.less";

type FilterMode = "active" | "inactive";

const Selgere: React.FC = () => {
    const [agents, setAgents] = useState<SalesAgent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<FilterMode>("active");
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingAgent, setEditingAgent] = useState<SalesAgent | null>(null);
    const [passwordAgent, setPasswordAgent] = useState<SalesAgent | null>(null);
    const [confirmAction, setConfirmAction] = useState<{
        agent: SalesAgent;
        type: "activate" | "deactivate" | "remove";
    } | null>(null);
    const salesAgent = useSelector((state: RootState) => getSalesAgent(state));
    const isManager = salesAgent?.isManager === true;
    const {
        activating,
        // deactivating,
        deletingEmployment,
        activateError,
        handleActivate,
        handleDeactivate,
        handleDeleteEmployment,
    } = useSalesAgentActions(setAgents);

    useEffect(() => {
        retailerFetch(API_URL_GET_SALES_AGENTS)
            .then((res) => {
                if (!res.ok) throw new Error(`Feil ved henting av selgere (${res.status})`);
                return res.json() as Promise<{ agents: SalesAgent[] }>;
            })
            .then((data) => setAgents(data.agents))
            .catch((err: Error) => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    const visibleAgents = filter === "active" ? agents.filter((a) => a.active) : agents.filter((a) => !a.active);

    return (
        <div className="retailer-dashboard-wrapper--big">
            <Heading mb="100" as="h1" variant="title-300">
                Selgere
            </Heading>

            {loading && (
                <Flex justify="center" pt="400">
                    <Spinner size="lg" />
                </Flex>
            )}

            {error && (
                <Notification type="tile" variant="error" mt="300">
                    <Notification.Icon />
                    <Notification.Text>{error}</Notification.Text>
                </Notification>
            )}
            {activateError && (
                <Notification type="tile" variant="error" mt="200">
                    <Notification.Icon />
                    <Notification.Text>{activateError}</Notification.Text>
                </Notification>
            )}

            {!loading && !error && isManager && (
                <Flex gap="200" mt="300" align="center" justify="between">
                    <Chip
                        type="single"
                        value={filter}
                        onValueChange={(val) => val && setFilter(val as FilterMode)}
                        variant="filter"
                    >
                        <Chip.Item value="active">Vis aktive</Chip.Item>
                        <Chip.Item value="inactive">Vis deaktiverte</Chip.Item>
                    </Chip>

                    <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                        Opprett ny selger
                    </Button>
                </Flex>
            )}
            {!loading && !error && visibleAgents.length === 0 && (
                <Text variant="additional-100" mt="300">
                    Ingen selgere funnet.
                </Text>
            )}

            {!loading && !error && visibleAgents.length > 0 && (
                <table className="selgere__table">
                    <thead>
                        <tr>
                            <th>Navn</th>
                            <th>E-post</th>
                            <th>Telefon</th>
                            <th>Leder</th>
                            <th className="selgere__table--actions" />
                        </tr>
                    </thead>
                    <tbody>
                        {visibleAgents.map((agent) => (
                            <tr
                                {...(isManager && filter === "active"
                                    ? { role: "button", "aria-label": `Rediger ${agent.firstName} ${agent.lastName}` }
                                    : {})}
                                key={agent.id}
                                onClick={() => isManager && filter === "active" && setEditingAgent(agent)}
                                onKeyDown={(e) => {
                                    if (isManager && filter === "active" && (e.key === "Enter" || e.key === " ")) {
                                        e.preventDefault();
                                        setEditingAgent(agent);
                                    }
                                }}
                                tabIndex={isManager && filter === "active" ? 0 : undefined}
                                className={isManager && filter === "active" ? "selgere__row--clickable" : undefined}
                            >
                                <td>
                                    {agent.firstName} {agent.lastName}
                                </td>
                                <td>{agent.email}</td>
                                <td>{agent.telephoneNumber ?? "—"}</td>
                                <td>{agent.manager ? "Ja" : "Nei"}</td>
                                <td>
                                    {!agent.active && isManager && (
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            loading={activating === agent.id}
                                            disabled={activating === agent.id}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setConfirmAction({ agent, type: "activate" });
                                            }}
                                        >
                                            Aktiver
                                        </Button>
                                    )}
                                    {agent.active && isManager && (
                                        <Flex gap="200">
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPasswordAgent(agent);
                                                }}
                                            >
                                                Sett passord
                                            </Button>
                                            {/* <Button
                                                variant="secondary"
                                                size="sm"
                                                loading={deactivating === agent.id}
                                                disabled={deactivating === agent.id}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setConfirmAction({ agent, type: "deactivate" });
                                                }}
                                            >
                                                Deaktiver
                                            </Button> */}
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                loading={deletingEmployment === agent.id}
                                                disabled={deletingEmployment === agent.id}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setConfirmAction({ agent, type: "remove" });
                                                }}
                                            >
                                                Fjern fra butikk
                                            </Button>
                                        </Flex>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {showCreateModal && (
                <CreateSalesAgentModal
                    onClose={() => setShowCreateModal(false)}
                    onCreated={(agent) => setAgents((prev) => [...prev, agent])}
                />
            )}
            {editingAgent && (
                <CreateSalesAgentModal
                    initialAgent={editingAgent}
                    onClose={() => setEditingAgent(null)}
                    onCreated={() => undefined}
                    onUpdated={(updated) => {
                        setAgents((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
                        setEditingAgent(null);
                    }}
                />
            )}
            {passwordAgent && <SetPasswordModal agent={passwordAgent} onClose={() => setPasswordAgent(null)} />}
            {confirmAction && (
                <Modal defaultOpen open>
                    <Modal.Overlay>
                        <Modal.Content>
                            <Modal.Title as="h3">
                                {confirmAction.type === "activate"
                                    ? "Aktiver selger"
                                    : confirmAction.type === "deactivate"
                                      ? "Deaktiver selger"
                                      : "Fjern selger fra forhandler"}
                            </Modal.Title>
                            <Modal.Group>
                                <Modal.Description>
                                    {confirmAction.type === "activate"
                                        ? `Er du sikker på at du ønsker å aktivere ${confirmAction.agent.firstName} ${confirmAction.agent.lastName}?`
                                        : confirmAction.type === "deactivate"
                                          ? `Er du sikker på at du ønsker å deaktivere ${confirmAction.agent.firstName} ${confirmAction.agent.lastName}?`
                                          : `Er du sikker på at du ønsker å fjerne ${confirmAction.agent.firstName} ${confirmAction.agent.lastName} fra denne forhandleren?`}
                                </Modal.Description>
                            </Modal.Group>
                            <Modal.Close aria-label="lukk" slot="floating" onClick={() => setConfirmAction(null)} />
                            <Modal.Group direction="column" variant="action">
                                <Flex gap="200" mt="300">
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            if (confirmAction.type === "activate") {
                                                handleActivate(confirmAction.agent);
                                            } else if (confirmAction.type === "deactivate") {
                                                handleDeactivate(confirmAction.agent);
                                            } else {
                                                handleDeleteEmployment(confirmAction.agent);
                                            }
                                            setConfirmAction(null);
                                        }}
                                    >
                                        Bekreft
                                    </Button>
                                    <Button variant="secondary" onClick={() => setConfirmAction(null)}>
                                        Avbryt
                                    </Button>
                                </Flex>
                            </Modal.Group>
                        </Modal.Content>
                    </Modal.Overlay>
                </Modal>
            )}
        </div>
    );
};

export default Selgere;
